import { warning } from 'reakit-utils/warning';
import { useState, useEffect, useCallback, useContext, useMemo, createElement } from 'react';
import 'reakit-utils/useId';
import { createComponent } from 'reakit-system/createComponent';
import { createHook } from 'reakit-system/createHook';
import { useBox } from '../Box/Box.js';
import { _ as _objectWithoutPropertiesLoose, a as _objectSpread } from '../chunk-d978b7c6.js';
import 'reakit-utils/mergeRefs';
import 'reakit-utils/useLiveRef';
import { useAllCallbacks } from 'reakit-utils/useAllCallbacks';
import '../Tabbable/Tabbable.js';
import 'reakit-utils/useSealedState';
import { useCreateElement } from 'reakit-system/useCreateElement';
import { usePipe } from 'reakit-utils/usePipe';
import '../Hidden/HiddenState.js';
import 'reakit-utils/useUpdateEffect';
import '../Dialog/DialogState.js';
import 'reakit-utils/createOnKeyDown';
import '../Rover/RoverState.js';
import '../Rover/Rover.js';
import 'popper.js';
import '../Popover/PopoverState.js';
import { M as MenuContext } from '../chunk-a9fa26a4.js';
import { useMenuState } from './MenuState.js';

function useShortcuts(_ref, timeout) {
  var stops = _ref.stops,
      move = _ref.move;

  if (timeout === void 0) {
    timeout = 500;
  }

  var _React$useState = useState(""),
      keys = _React$useState[0],
      setKeys = _React$useState[1];

  useEffect(function () {
    if (!keys) return undefined;
    var timeoutId = setTimeout(function () {
      return setKeys("");
    }, timeout);
    var stop = stops.find(function (s) {
      return Boolean(s.ref.current && s.ref.current.textContent && s.ref.current.textContent.toLowerCase().startsWith(keys));
    });

    if (stop) {
      move(stop.id);
    }

    return function () {
      return clearTimeout(timeoutId);
    };
  }, [keys, stops, move, timeout]);
  var onKeyDown = useCallback(function (event) {
    if (event.metaKey || event.altKey || event.shiftKey || event.ctrlKey) {
      return;
    }

    if (/^[a-z0-9_-]$/i.test(event.key)) {
      event.stopPropagation();
      event.preventDefault();
      setKeys("" + keys + event.key);
    }
  }, [keys, setKeys]);
  return onKeyDown;
}

var useStaticMenu = createHook({
  name: "StaticMenu",
  compose: useBox,
  useState: useMenuState,
  useProps: function useProps(options, _ref) {
    var htmlOnKeyDown = _ref.onKeyDown,
        htmlWrap = _ref.unstable_wrap,
        htmlProps = _objectWithoutPropertiesLoose(_ref, ["onKeyDown", "unstable_wrap"]);

    var parent = useContext(MenuContext);
    var providerValue = useMemo(function () {
      return {
        orientation: options.orientation,
        next: options.next,
        previous: options.previous,
        parent: parent
      };
    }, [options.orientation, options.next, options.previous, parent]);
    var onKeyDown = useShortcuts(options);
    var wrap = useCallback(function (children) {
      return createElement(MenuContext.Provider, {
        value: providerValue
      }, children);
    }, [providerValue]);
    return _objectSpread({
      role: options.orientation === "horizontal" ? "menubar" : "menu",
      "aria-orientation": options.orientation,
      onKeyDown: useAllCallbacks(onKeyDown, htmlOnKeyDown),
      unstable_wrap: usePipe(wrap, htmlWrap)
    }, htmlProps);
  }
});
var StaticMenu = createComponent({
  as: "div",
  useHook: useStaticMenu,
  useCreateElement: function useCreateElement$1(type, props, children) {
    process.env.NODE_ENV !== "production" ? warning(!props["aria-label"] && !props["aria-labelledby"] && props.role !== "menubar", "Menu", "You should provide either `aria-label` or `aria-labelledby` props.", "See https://reakit.io/docs/menu") : void 0;
    return useCreateElement(type, props, children);
  }
});

export { StaticMenu, useStaticMenu };
